import React, { useMemo, useState, useEffect, useCallback } from 'react';
import InvestmentSectionTabs from './InvestmentSectionTabs';
import {
    TRANCHE_SLOT_MAX,
    LINK_STARTUP_OVERVIEW_TAB,
    applyIssuePriceToAllRowsState,
    cloneTrancheSlotsFromForm,
    createEmptyTrancheSlots,
    getTrancheTabColumns,
    investorGrossSumFromForm,
    isEnfanceAggregateLinkMode,
    linkModalInvestorList,
    linkOverviewColumnsForInvestmentType,
    linkSelfInvestmentOptional,
    linkTrancheCount,
    linkUsesIssuePriceAndPartlyPaid,
    normalizeTrancheSplitPcts,
    patchLinkStartupInvestmentFormState,
    readTrancheCell,
    recomputeOverviewDerived,
    selfNetworkAmountTitle,
    shouldAutoFillSelfInvestment
} from '../../lib/networkLinkCapTableForm';

/**
 * Compact round setup + overview/tranche tabs + cap table (same UX as investor-network “link startup & investments”).
 */
export default function NetworkLinkStyleCapTableEditor({
    form,
    setForm,
    allInvestors,
    user,
    isManagementUser = false,
    isAdminMode = false,
    effectiveNetworkId,
    onSavePartlyDefault,
    showNotReadyCheckbox = true
}) {
    const [activeSection, setActiveSection] = useState(LINK_STARTUP_OVERVIEW_TAB.id);

    const linkModalInvestors = useMemo(() => linkModalInvestorList(allInvestors, form), [allInvestors, form]);

    const linkStartupSectionTabsEffective = useMemo(() => {
        const tc = linkTrancheCount(form);
        const tabs = [LINK_STARTUP_OVERVIEW_TAB];
        for (let k = 1; k <= tc; k++) {
            tabs.push({ id: `tranche-${k}`, label: `Tranche ${k}` });
        }
        return tabs;
    }, [form.trancheCount]);

    const currentColumns = useMemo(() => {
        const usePricing = linkUsesIssuePriceAndPartlyPaid(form.investmentType);
        if (activeSection === 'overview') {
            return linkOverviewColumnsForInvestmentType(form.investmentType);
        }
        const m = /^tranche-(\d+)$/.exec(activeSection || '');
        if (m) {
            const idx = parseInt(m[1], 10) - 1;
            if (idx >= 0 && idx < TRANCHE_SLOT_MAX) {
                const cols = getTrancheTabColumns(idx);
                return usePricing ? cols : cols.filter((c) => c.tranchePart === 'Sub');
            }
        }
        return linkOverviewColumnsForInvestmentType(form.investmentType);
    }, [activeSection, form.investmentType]);

    useEffect(() => {
        const m = /^tranche-(\d+)$/.exec(activeSection || '');
        if (!m) return;
        const n = parseInt(m[1], 10);
        const tc = linkTrancheCount(form);
        if (n > tc) setActiveSection(LINK_STARTUP_OVERVIEW_TAB.id);
    }, [form, activeSection]);

    const displayForm = useMemo(() => recomputeOverviewDerived(form, linkModalInvestors), [form, linkModalInvestors]);

    const linkModalFooterSums = useMemo(() => {
        const enfance = isEnfanceAggregateLinkMode(form, linkModalInvestors);
        const invSum = investorGrossSumFromForm(form, linkModalInvestors);
        const netAmt = parseFloat(form.networkAmount) || 0;
        const allocated = enfance ? invSum : invSum + netAmt;
        const total = parseFloat(form.totalAmount) || 0;
        const selfOpt = linkSelfInvestmentOptional(form.investmentType);
        return {
            allocated,
            remaining: Math.max(total - allocated, 0),
            allocatedLabel: enfance
                ? 'Allocated (investor gross):'
                : selfOpt
                  ? 'Allocated (investors + optional self):'
                  : 'Allocated total:'
        };
    }, [form, linkModalInvestors]);

    const investorsAvailableToAddToDeal = useMemo(() => {
        if (!Array.isArray(form.includedInvestorIds)) return [];
        return allInvestors.filter((inv) => !form.includedInvestorIds.includes(inv.id));
    }, [allInvestors, form.includedInvestorIds]);

    const addInvestorToLinkDeal = useCallback(
        (invId) => {
            setForm((f) => {
                if (!Array.isArray(f.includedInvestorIds)) return f;
                if (f.includedInvestorIds.includes(invId)) return f;
                return { ...f, includedInvestorIds: [...f.includedInvestorIds, invId] };
            });
        },
        [setForm]
    );

    const removeInvestorFromLinkDeal = useCallback(
        (invId) => {
            setForm((f) => {
                const cur = Array.isArray(f.includedInvestorIds) ? f.includedInvestorIds : allInvestors.map((i) => i.id);
                return { ...f, includedInvestorIds: cur.filter((id) => id !== invId) };
            });
        },
        [setForm, allInvestors]
    );

    const patchCell = useCallback(
        (column, kind, invId, rawValue) => {
            setForm((f) => patchLinkStartupInvestmentFormState(f, column, kind, invId, rawValue));
        },
        [setForm]
    );

    const applyIssuePriceToAllRows = useCallback(
        (raw) => {
            setForm((f) => applyIssuePriceToAllRowsState(f, raw, allInvestors));
        },
        [setForm, allInvestors]
    );

    const canSavePartlyDefault =
        typeof onSavePartlyDefault === 'function' &&
        (user?.role === 'INVESTOR_NETWORK' || isManagementUser || (isAdminMode && effectiveNetworkId));

    if (!Array.isArray(allInvestors)) {
        return <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">Invalid investor list.</p>;
    }

    return (
        <div className="space-y-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50/90 px-2 py-1.5 sm:px-2.5 sm:py-1.5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide shrink-0">Round setup</span>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <label htmlFor="link-inv-stage" className="text-gray-600 shrink-0">
                            Stage
                        </label>
                        <select
                            id="link-inv-stage"
                            value={form.investmentStage}
                            onChange={(e) => setForm((f) => ({ ...f, investmentStage: e.target.value }))}
                            className="w-[6.75rem] sm:w-28 px-1.5 py-0.5 text-xs border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="SEED">Seed</option>
                            <option value="SERIES_A">Series A</option>
                            <option value="SERIES_B">Series B</option>
                            <option value="SERIES_C">Series C</option>
                            <option value="GROWTH">Growth</option>
                        </select>
                        <label htmlFor="link-inv-currency" className="text-gray-600 shrink-0 ml-1 sm:ml-2">
                            Currency
                        </label>
                        <select
                            id="link-inv-currency"
                            value={form.currency}
                            onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                            className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="INR">INR</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                        </select>
                        <label htmlFor="link-inv-type" className="text-gray-600 shrink-0 ml-1 sm:ml-2">
                            Type
                        </label>
                        <select
                            id="link-inv-type"
                            value={form.investmentType}
                            onChange={(e) => {
                                const v = e.target.value;
                                setForm((f) => {
                                    const cleared = linkUsesIssuePriceAndPartlyPaid(v)
                                        ? {}
                                        : { partlyPaidAmountPercent: '', networkShareValue: '', shareValues: {} };
                                    return { ...f, investmentType: v, ...cleared };
                                });
                            }}
                            className="w-[6.5rem] sm:w-28 px-1.5 py-0.5 text-xs border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="EQUITY">Equity</option>
                            <option value="CCPS">CCPS</option>
                            <option value="CCD">CCD</option>
                            <option value="CONVERTIBLE_NOTE">Conv. Note</option>
                            <option value="SAFE">SAFE</option>
                        </select>
                    </div>
                    <span className="hidden sm:inline h-3 w-px bg-gray-300 shrink-0" aria-hidden />
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 w-full sm:w-auto sm:flex-1 min-w-0">
                        <label htmlFor="link-inv-total" className="text-gray-600 shrink-0">
                            Total
                        </label>
                        <input
                            id="link-inv-total"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Round amount"
                            value={form.totalAmount}
                            onChange={(e) => setForm((f) => ({ ...f, totalAmount: e.target.value }))}
                            className="w-24 sm:w-28 min-w-0 px-1.5 py-0.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 text-right"
                            title={
                                isEnfanceAggregateLinkMode(form, linkModalInvestors)
                                    ? 'Round total = sum of investors’ gross committed (self row is derived separately).'
                                    : linkUsesIssuePriceAndPartlyPaid(form.investmentType)
                                      ? 'Round total; with no investor gross yet, self investment = Total × Partly paid %.'
                                      : 'Full round amount to match against allocated investor and self amounts.'
                            }
                        />
                        {linkUsesIssuePriceAndPartlyPaid(form.investmentType) && (
                            <>
                                <label htmlFor="link-inv-issue-price" className="text-gray-600 shrink-0 ml-1 sm:ml-2">
                                    Issue price
                                </label>
                                <input
                                    id="link-inv-issue-price"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="/ share"
                                    value={form.networkShareValue}
                                    onChange={(e) => applyIssuePriceToAllRows(e.target.value)}
                                    className="w-20 sm:w-24 min-w-0 px-1.5 py-0.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 text-right"
                                    title="Applies to network self and all investors for CCPS and entry share price"
                                />
                                <label htmlFor="link-inv-partly" className="text-gray-600 shrink-0 ml-1 sm:ml-2">
                                    Partly %
                                </label>
                                <input
                                    id="link-inv-partly"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0–100"
                                    value={form.partlyPaidAmountPercent}
                                    onChange={(e) => setForm((f) => ({ ...f, partlyPaidAmountPercent: e.target.value }))}
                                    className="w-14 sm:w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 text-right"
                                    title="Applied to each investor’s gross committed: partly paid = gross × %, nett = gross − partly. When investor gross is entered, the self row rolls up those splits; otherwise self = Total × this %."
                                />
                                {canSavePartlyDefault && (
                                    <button
                                        type="button"
                                        onClick={() => onSavePartlyDefault(form.partlyPaidAmountPercent)}
                                        className="px-2 py-0.5 text-[10px] font-medium border border-gray-300 rounded-md text-gray-800 bg-white hover:bg-gray-50 whitespace-nowrap shrink-0"
                                        title="Save partly paid % as default for future deals"
                                    >
                                        Save default
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 flex flex-wrap justify-center">
                <InvestmentSectionTabs tabs={linkStartupSectionTabsEffective} activeTab={activeSection} onChange={setActiveSection} />
            </div>

            {activeSection === 'overview' && (
                <div className="rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs">
                    <p className="font-medium text-gray-800 mb-1.5">Tranche split (%)</p>
                    <div className="flex flex-wrap gap-3 items-end">
                        {Array.from({ length: linkTrancheCount(form) }, (_, i) => (
                            <div key={`split-${i}`} className="flex flex-col gap-0.5">
                                <label className="text-gray-600">Tranche {i + 1}</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                                    value={form.trancheSplitPcts?.[i] ?? ''}
                                    onChange={(e) =>
                                        setForm((f) => {
                                            const pcts = normalizeTrancheSplitPcts(f);
                                            pcts[i] = e.target.value;
                                            const patched = { ...f, trancheSplitPcts: pcts };
                                            return recomputeOverviewDerived(patched, linkModalInvestorList(allInvestors, patched));
                                        })
                                    }
                                    title="Weight for splitting nett (or gross) into this tranche’s subscription row"
                                />
                            </div>
                        ))}
                        <div className="flex flex-wrap gap-2 items-center pb-0.5">
                            {linkTrancheCount(form) < TRANCHE_SLOT_MAX && (
                                <button
                                    type="button"
                                    className="px-2 py-1 text-xs font-medium rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
                                    onClick={() =>
                                        setForm((f) => ({
                                            ...f,
                                            trancheCount: Math.min(TRANCHE_SLOT_MAX, linkTrancheCount(f) + 1)
                                        }))
                                    }
                                >
                                    Add tranche
                                </button>
                            )}
                            {linkTrancheCount(form) > 1 && (
                                <button
                                    type="button"
                                    className="px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                                    onClick={() =>
                                        setForm((f) => {
                                            const tc = linkTrancheCount(f);
                                            if (tc <= 1) return f;
                                            const newTc = tc - 1;
                                            const slots = cloneTrancheSlotsFromForm(f);
                                            for (let j = newTc; j < TRANCHE_SLOT_MAX; j++) {
                                                slots[j] = {
                                                    networkSub: '',
                                                    networkCcps: '',
                                                    networkHolding: '',
                                                    invSub: {},
                                                    invCcps: {},
                                                    invHolding: {}
                                                };
                                            }
                                            return { ...f, trancheCount: newTc, trancheSlots: slots };
                                        })
                                    }
                                >
                                    Remove last tranche
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="text-gray-500 mt-2">
                        Up to {TRANCHE_SLOT_MAX} tranches. When any weight is greater than 0, each row’s subscription amounts fill from nett
                        subscription (or gross if nett is empty), split by these weights. Set all to 0 to enter tranche amounts manually.
                    </p>
                </div>
            )}

            {investorsAvailableToAddToDeal.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-gray-600 font-medium">Add investor</span>
                    <select
                        className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white min-w-[12rem]"
                        defaultValue=""
                        onChange={(e) => {
                            const v = e.target.value;
                            if (!v) return;
                            const inv = investorsAvailableToAddToDeal.find((x) => String(x.id) === v);
                            if (inv) addInvestorToLinkDeal(inv.id);
                            e.target.value = '';
                        }}
                    >
                        <option value="">+ Select investor…</option>
                        {investorsAvailableToAddToDeal.map((inv) => (
                            <option key={inv.id} value={String(inv.id)}>
                                {inv.name || inv.email}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {linkModalInvestors.length === 0 && allInvestors.length > 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded-lg">
                    No investors in this deal. Use &quot;Add investor&quot; to include someone who will invest.
                </p>
            )}

            <div className="overflow-x-auto">
                <table className="min-w-full w-full text-xs border-collapse">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="sticky left-0 z-10 bg-gray-50 border border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700 min-w-[160px]">
                                Investor
                            </th>
                            {currentColumns.map((column) => (
                                <th
                                    key={column.key || column.label}
                                    title={column.columnHint}
                                    className="border border-gray-200 px-2 py-1.5 text-right font-semibold text-gray-700 min-w-[150px]"
                                >
                                    <span className="block">{column.label}</span>
                                    {column.columnSubLabel ? (
                                        <span className="block text-[10px] font-normal text-gray-500 mt-0.5 leading-tight">{column.columnSubLabel}</span>
                                    ) : null}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {linkModalInvestors.map((inv) => (
                            <tr key={inv.id} className="hover:bg-gray-50">
                                <td className="sticky left-0 z-10 bg-white hover:bg-gray-50 border border-gray-200 px-2 py-1.5">
                                    <p className="font-medium text-gray-900 truncate">{inv.name}</p>
                                    <p className="text-xs text-gray-400 truncate">{inv.email}</p>
                                    <button
                                        type="button"
                                        className="mt-1 text-xs text-red-600 hover:underline"
                                        onClick={() => removeInvestorFromLinkDeal(inv.id)}
                                    >
                                        Remove from deal
                                    </button>
                                </td>
                                {currentColumns.map((column) => {
                                    const colKey = column.key || column.label;
                                    const invCellVal =
                                        column.trancheSlotIndex != null
                                            ? readTrancheCell(
                                                  displayForm.trancheSlots?.[column.trancheSlotIndex],
                                                  column.tranchePart,
                                                  inv.id
                                              )
                                            : (displayForm[column.stateKey] || {})[inv.id] ?? '';
                                    return (
                                        <td key={colKey} className="border border-gray-200 px-1 py-1">
                                            {column.placeholder ? (
                                                <span className="text-gray-400 text-xs px-1">{column.placeholder}</span>
                                            ) : (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step={column.step}
                                                    value={invCellVal}
                                                    onChange={(e) => patchCell(column, 'inv', inv.id, e.target.value)}
                                                    readOnly={!!column.derived}
                                                    title={
                                                        column.derived
                                                            ? column.derivedTitle
                                                            : column.tranchePart === 'Sub' && column.derivedTitle
                                                              ? column.derivedTitle
                                                              : undefined
                                                    }
                                                    className={`w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-right ${
                                                        column.derived ? 'bg-gray-100 cursor-not-allowed' : ''
                                                    }`}
                                                />
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        <tr className="bg-blue-50">
                            <td className="sticky left-0 z-10 bg-blue-50 border border-gray-200 px-2 py-1.5">
                                <p className="font-medium text-gray-900 truncate">{user?.organizationName || user?.name || 'Network'}</p>
                                <p className="text-xs text-gray-400">
                                    {linkSelfInvestmentOptional(form.investmentType) ? 'Self investment (optional)' : 'Self investment'}
                                </p>
                            </td>
                            {currentColumns.map((column) => {
                                const colKey = column.key || column.label;
                                const netCellVal =
                                    column.trancheSlotIndex != null
                                        ? readTrancheCell(
                                              displayForm.trancheSlots?.[column.trancheSlotIndex],
                                              column.tranchePart,
                                              null
                                          )
                                        : displayForm[column.networkKey] ?? '';
                                return (
                                    <td key={colKey} className="border border-gray-200 px-1 py-1">
                                        {column.placeholder ? (
                                            <span className="text-gray-400 text-xs px-1">{column.placeholder}</span>
                                        ) : (
                                            <input
                                                type="number"
                                                min="0"
                                                step={column.step}
                                                value={netCellVal}
                                                onChange={(e) => patchCell(column, 'network', null, e.target.value)}
                                                readOnly={
                                                    (column.networkKey === 'networkAmount' && shouldAutoFillSelfInvestment(form, linkModalInvestors)) ||
                                                    !!column.derived
                                                }
                                                title={
                                                    column.networkKey === 'networkAmount'
                                                        ? [
                                                              selfNetworkAmountTitle(form, linkModalInvestors),
                                                              linkSelfInvestmentOptional(form.investmentType)
                                                                  ? 'Optional: include only if the network invests alongside investors.'
                                                                  : null
                                                          ]
                                                              .filter(Boolean)
                                                              .join(' ')
                                                        : column.derived
                                                          ? column.derivedTitle
                                                          : column.tranchePart === 'Sub' && column.derivedTitle
                                                            ? column.derivedTitle
                                                            : undefined
                                                }
                                                className={`w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-right ${
                                                    (column.networkKey === 'networkAmount' &&
                                                        shouldAutoFillSelfInvestment(form, linkModalInvestors)) ||
                                                    column.derived
                                                        ? 'bg-gray-100 cursor-not-allowed'
                                                        : ''
                                                }`}
                                            />
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="mt-2 p-2 rounded-lg bg-gray-50 border border-gray-200 text-xs flex flex-wrap gap-x-6 gap-y-1">
                <div className="flex items-center gap-2">
                    <span className="text-gray-600">{linkModalFooterSums.allocatedLabel}</span>
                    <span className="font-semibold text-gray-900">{linkModalFooterSums.allocated.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-gray-600">Remaining:</span>
                    <span className="font-semibold text-gray-900">{linkModalFooterSums.remaining.toLocaleString('en-IN')}</span>
                </div>
            </div>

            {showNotReadyCheckbox && (
                <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={!!form.notReadyToInvestYet}
                        onChange={(e) => setForm((f) => ({ ...f, notReadyToInvestYet: e.target.checked }))}
                        className="rounded border-gray-300"
                    />
                    Some investors are not ready to invest yet (link startup now and record investments later)
                </label>
            )}
        </div>
    );
}
